import React, { useState, useEffect, useRef } from 'react';
import ToHomeButton from './buttons/ToHomeButton';
import PlayButton from './buttons/PlayButton';
import ShowHintButton from './buttons/ShowHintButton';
import { useExerciseModels } from './helpers/exercise/useExerciseModels';
import ShapeStepColumn from './helpers/exercise/ShapeStepColumn';
import ExerciseWorkspace from './helpers/exercise/ExerciseWorkspace';
import SelfExpPanel from './helpers/exercise/SelfExpPanel';
import StepGuide, { SIMULATION_STEPS, simulationStep } from './helpers/exercise/StepGuide';
import { useHintCamera } from './helpers/exercise/useHintCamera';
import { logEvent } from '../utils/experimentLog';
import { defaultCameraPosition } from './helpers/cameraPositions';

// 中間状態のジオメトリ変形量（0=モチーフ、0.5=誤り、0.2=中間）
const INTERMEDIATE_SHEAR = 0.2;

// 形状変更演習の画面。PlayButton で 誤り→中間→モチーフ の3ステップを切り替える。
// レイアウト（左：シミュレーション／右：説明シート）は ExerciseWorkspace、
// 各ステップの描画は helpers/exercise/ShapeStepColumn が担い、
// ここではステップ定義（モデル名・canvasId 等の差分）と状態の編成のみを行う。
function ShapeChange({
  number,
  selectedPart,
  isOrbitControlsEnabled = true,
  drawingImages = [null, null, null],
  imagesLoading = true,
  errorGlbPath: errorGlbPathProp = 'model1',
  initialCameraPosition = null,
  isLastQuestion = false,
  // 回答済みの問へ戻ってきたとき：当時の回答を復元表示し、【決定】は押させない
  answer = null,
  locked = false,
  onAnswered = null, // 【決定】で送信した時点で呼ぶ（親が回答済みとして保存する）
  onComplete = null,
}) {
  const { errorGlbPath, motifGlbPath } = useExerciseModels(errorGlbPathProp);
  // onComplete を渡さない呼び出し（/suggest2 の研究発表用デモ）はデモ表示扱い
  const isDemo = typeof onComplete !== 'function';

  const [stepValue, setStepValue] = useState(0);
  // 手順の進み具合：モチーフまで見たか／説明シートの空欄をすべて選んだか
  const [sawMotif, setSawMotif] = useState(false);
  const [filled, setFilled] = useState(false);
  const [cameraPosition, setCameraPosition] = useState(() => initialCameraPosition || defaultCameraPosition);

  // 親から初期カメラ位置が渡されたらセット（PartSelectの指定を初期位置として使う）
  useEffect(() => {
    if (initialCameraPosition) setCameraPosition(initialCameraPosition);
  }, [initialCameraPosition]);

  // 3ステップの定義。共通propsは下の viewerProps 合成で付与する
  const steps = [
    {
      label: '誤り',
      viewerProps: { glbPath: errorGlbPath, imagePath: './drawing/model1.png', caption: '右', canvasId: 'rightModelCanvas' },
    },
    {
      label: '中間',
      viewerProps: { glbPath: errorGlbPath, caption: 'モチーフ+2', canvasId: 'ProcessModelCanvas', shearValue: INTERMEDIATE_SHEAR },
    },
    {
      label: 'モチーフ',
      viewerProps: { glbPath: motifGlbPath, imagePath: `./drawing/${motifGlbPath}.png`, caption: '左', canvasId: 'leftModelCanvas' },
    },
  ];
  const stepLabels = steps.map((s) => s.label);
  const step = steps[stepValue];

  // ヒント：表示中のステップの3Dモデルを「おかしさが分かる視点」へ動かす（useHintCamera）。
  // 移動した視点は OrbitControls 経由で CameraContext に載るので、次のステップへ進んでも
  // この視点のまま表示される（ドラッグで回したときと同じ）。
  // 移動中に【次のステップ】を押すとキャンバスが作り直されて動きの途中の視点が引き継がれるため、移動中は押させない
  const viewerRef = useRef(null);
  const { animating: hintAnimating, shown: hintShown, showHint, resetView } = useHintCamera([viewerRef], isOrbitControlsEnabled, errorGlbPath);

  return (
    <>
      <ExerciseWorkspace
        eyebrow="形状変更シミュレーション"
        number={number}
        instruction={locked
          ? 'この問題は回答済みです。シミュレーションと説明を見直せます。'
          : <StepGuide steps={SIMULATION_STEPS.shape} current={simulationStep(sawMotif, filled)} done={[sawMotif, filled]} />}
        aside={
          <SelfExpPanel
            number={number}
            phase="shape-change"
            errorGlbPath={errorGlbPath}
            selectedPart={selectedPart}
            isLastQuestion={isLastQuestion}
            // onComplete を渡さない＝デモ表示。シートは見せるが【決定】は効かせない
            demo={isDemo}
            initialAnswer={answer}
            locked={locked}
            onSelectionChange={(r) => setFilled(Boolean(r.complete))}
            onSubmitted={isDemo ? null : onAnswered}
            onAdvance={isDemo ? null : (selfText, detail) => onComplete(selectedPart, cameraPosition, selfText, detail)}
          />
        }
      >
        {/* key=stepValue：ステップ切替時はキャンバスごと作り直す（従来の条件分岐レンダーと同じ挙動） */}
        <ShapeStepColumn
          key={stepValue}
          viewerRef={viewerRef}
          label={step.label}
          drawingImage={drawingImages && drawingImages[stepValue]}
          imagesLoading={imagesLoading}
          viewerProps={{
            ...step.viewerProps,
            cameraPosition,
            addColor: selectedPart,
            isOrbitControlsEnabled,
          }}
        />

        <div className="row-center mt-4 gap-4">
          <PlayButton
            StepValue={stepValue}
            setStepValue={(v) => {
              logEvent('shape_step_change', {
                questionNumber: number ?? null,
                glbPath: errorGlbPath,
                step: v,
                stepLabel: stepLabels[v],
              });
              setStepValue(v);
              if (v === stepLabels.length - 1) setSawMotif(true);
            }}
            new_cameraPosition={cameraPosition}
            setCameraPosition={setCameraPosition}
            stepLabels={stepLabels}
            disabled={hintAnimating}
          />
          <ShowHintButton glbPath={errorGlbPath} onShow={showHint} onReset={resetView} shown={hintShown} disabled={hintAnimating} />
        </div>
      </ExerciseWorkspace>

      <div className="row-center">
        <ToHomeButton buttonText="Home画面に戻る" />
      </div>
    </>
  );
}

export default ShapeChange;
